import { LucideIcon, MoreHorizontal } from "lucide-react";
import React from "react";

import "./styles.scss";

export type TableRowActionIcon = LucideIcon;

export interface TableRowActionItem {
  key: string;
  label: string;
  icon: TableRowActionIcon;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  isDanger?: boolean;
}

export interface TableRowActionsProps {
  actions: TableRowActionItem[];
  isMenuOpen?: boolean;
  onToggleMenu?: () => void;
  moreActionsLabel?: string;
}

const renderIconAction = (
  action: TableRowActionItem,
  variant: "inline" | "menu-item",
  onAfterClick?: () => void
) => {
  const Icon = action.icon;
  const className =
    variant === "inline"
      ? `table-row-action-btn${action.isDanger ? " is-danger" : ""}`
      : `table-row-actions-dropdown-item${action.isDanger ? " is-danger" : ""}`;

  const handleClick = () => {
    action.onClick?.();
    onAfterClick?.();
  };

  const content =
    variant === "inline" ? (
      <Icon size={16} aria-hidden={true} />
    ) : (
      <>
        <Icon size={16} aria-hidden={true} />
        <span>{action.label}</span>
      </>
    );

  if (action.href) {
    return (
      <a
        key={action.key}
        href={action.href}
        target={action.target}
        rel={action.rel}
        className={className}
        title={variant === "inline" ? action.label : undefined}
        aria-label={variant === "inline" ? action.label : undefined}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      key={action.key}
      type="button"
      className={className}
      title={variant === "inline" ? action.label : undefined}
      aria-label={variant === "inline" ? action.label : undefined}
      onClick={handleClick}
    >
      {content}
    </button>
  );
};

export const TableRowActions: React.FunctionComponent<TableRowActionsProps> = ({
  actions,
  isMenuOpen = false,
  onToggleMenu,
  moreActionsLabel = "操作メニューを開く",
}) => {
  if (actions.length === 0) {
    return <span className="table-row-actions-empty">-</span>;
  }

  if (actions.length <= 2) {
    return (
      <div
        className="table-row-actions"
        onClick={(event) => event.stopPropagation()}
      >
        {actions.map((action) => renderIconAction(action, "inline"))}
      </div>
    );
  }

  const [primaryAction, ...overflowActions] = actions;

  return (
    <div
      className="table-row-actions"
      onClick={(event) => event.stopPropagation()}
    >
      {renderIconAction(primaryAction, "inline")}
      <div className="table-row-actions-menu">
        <button
          type="button"
          className="table-row-actions-menu-trigger"
          aria-label={moreActionsLabel}
          aria-expanded={isMenuOpen}
          onClick={onToggleMenu}
        >
          <MoreHorizontal size={20} aria-hidden="true" />
        </button>
        {isMenuOpen && (
          <div className="table-row-actions-dropdown">
            {overflowActions.map((action) =>
              renderIconAction(action, "menu-item", onToggleMenu)
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TableRowActions;
